import React, { useState, useEffect } from 'react';
import { PencilIcon, TrashIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { Disease, DiseaseGroup } from '../../types/diseases';
import { diseasesApi, diseaseGroupsApi } from '../../api/diseases';
import toast from 'react-hot-toast';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface DiseasesListProps {
  onRefresh?: () => void;
}

const DiseasesList: React.FC<DiseasesListProps> = ({ onRefresh }) => {
  const [diseases, setDiseases] = useState<Disease[]>([]);
  const [groups, setGroups] = useState<DiseaseGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState<Disease | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [diseasesData, groupsData] = await Promise.all([
        diseasesApi.getAll(),
        diseaseGroupsApi.getAll()
      ]);
      setDiseases(diseasesData);
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading diseases:', error);
      toast.error('Failed to load diseases');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (disease: Disease) => {
    setShowEditModal(disease);
  };

  const handleUpdateDisease = async (diseaseId: number, data: any) => {
    try {
      await diseasesApi.update(diseaseId, data);
      setShowEditModal(null);
      await loadData();
      toast.success('Disease updated successfully');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating disease:', error);
      toast.error('Failed to update disease');
    }
  };

  const handleDelete = async (disease: Disease) => {
    if (window.confirm(`Are you sure you want to delete "${disease.name}"?`)) {
      try {
        await diseasesApi.delete(disease.id);
        await loadData();
        toast.success('Disease deleted successfully');
        if (onRefresh) onRefresh();
      } catch (error) {
        console.error('Error deleting disease:', error);
        toast.error('Failed to delete disease');
      }
    }
  };

  const getGroupName = (groupId: number) => {
    const group = groups.find((g) => g.id === groupId);
    return group?.name || 'Unknown Group';
  };

  const getSeverityBadge = (severity: string) => {
    const severityConfig = {
      LOW: { bg: 'bg-green-100', text: 'text-green-800', label: 'Low' },
      MEDIUM: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' },
      HIGH: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
      CRITICAL: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' }
    };
    const config = severityConfig[severity as keyof typeof severityConfig] || severityConfig.MEDIUM;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getMortalityBadge = (mortality: string) => {
    const mortalityConfig = {
      LOW: { bg: 'bg-green-100', text: 'text-green-800', label: 'Low' },
      MEDIUM: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' },
      HIGH: { bg: 'bg-red-100', text: 'text-red-800', label: 'High' }
    };
    const config = mortalityConfig[mortality as keyof typeof mortalityConfig] || mortalityConfig.LOW;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (diseases.length === 0) {
    return (
      <div className="text-center py-12">
        <BeakerIcon className="mx-auto h-12 w-12 text-slate-400" />
        <h3 className="mt-2 text-sm font-semibold text-slate-900">No diseases</h3>
        <p className="mt-1 text-sm text-slate-500">
          Get started by creating a new disease.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
            Diseases ({diseases.length})
          </h3>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-0"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Group
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Severity
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Mortality
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Contagious
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Symptoms
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900"
                      >
                        Affected Systems
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-0"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {diseases.map((disease) => (
                      <tr key={disease.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-slate-900 sm:pl-0">
                          {disease.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {getGroupName(disease.groupId)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {disease.severity ? getSeverityBadge(disease.severity) : '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {disease.mortality ? getMortalityBadge(disease.mortality) : '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                          {disease.contagious ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-800">
                              Yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                              No
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-500 max-w-xs truncate">
                          {disease.symptoms || '-'}
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-500 max-w-xs truncate">
                          {disease.affectedSystems?.join(', ') || '-'}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEdit(disease)}
                              className="text-cyan-600 hover:text-indigo-900"
                              title="Edit disease"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(disease)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete disease"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Disease Modal */}
      <BrandModal
        open={!!showEditModal}
        onClose={() => setShowEditModal(null)}
        maxWidthClass="max-w-4xl"
      >
        {showEditModal ? (
          <>
            <BrandModalHeader title="Edit Disease" onClose={() => setShowEditModal(null)} />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);

                // Collect affected systems from checkboxes
                const affectedSystems: string[] = [];
                const systemOptions = [
                  'Respiratory', 'Digestive', 'Nervous', 'Circulatory', 'Reproductive',
                  'Musculoskeletal', 'Integumentary', 'Urinary', 'Immune', 'Endocrine'
                ];

                systemOptions.forEach(system => {
                  if (formData.get(`affectedSystems-${system}`) === 'on') {
                    affectedSystems.push(system);
                  }
                });

                handleUpdateDisease(showEditModal.id, {
                  name: formData.get('name'),
                  groupId: parseInt(formData.get('groupId') as string),
                  description: formData.get('description'),
                  diagnosis: formData.get('diagnosis'),
                  cause: formData.get('cause'),
                  symptoms: formData.get('symptoms'),
                  treatments: formData.get('treatments'),
                  prevention: formData.get('prevention'),
                  contagious: formData.get('contagious') === 'on',
                  severity: formData.get('severity'),
                  mortality: formData.get('mortality'),
                  incubationPeriod: formData.get('incubationPeriod'),
                  notes: formData.get('notes'),
                  affectedSystems: affectedSystems,
                });
              }}
            >
              <BrandModalBody>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Disease Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      defaultValue={showEditModal.name}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Disease Group *
                    </label>
                    <select
                      name="groupId"
                      required
                      defaultValue={showEditModal.groupId}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="empty-option-1753461297301" value="">Select a group...</option>
                      {groups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Severity
                    </label>
                    <select
                      name="severity"
                      defaultValue={showEditModal.severity || 'MEDIUM'}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="LOW-1753461297301" value="LOW">Low</option>
                      <option key="MEDIUM-1753461297301" value="MEDIUM">Medium</option>
                      <option key="HIGH-1753461297301" value="HIGH">High</option>
                      <option key="CRITICAL-1753461297301" value="CRITICAL">Critical</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Mortality Rate
                    </label>
                    <select
                      name="mortality"
                      defaultValue={showEditModal.mortality || 'LOW'}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    >
                      <option key="LOW-1753461297301" value="LOW">Low</option>
                      <option key="MEDIUM-1753461297301" value="MEDIUM">Medium</option>
                      <option key="HIGH-1753461297301" value="HIGH">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Incubation Period
                    </label>
                    <input
                      type="text"
                      name="incubationPeriod"
                      defaultValue={showEditModal.incubationPeriod || ''}
                      placeholder="e.g., 3-7 days"
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="contagious"
                      defaultChecked={showEditModal.contagious}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-slate-900">
                      Contagious
                    </label>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Description
                    </label>
                    <textarea
                      name="description"
                      rows={3}
                      defaultValue={showEditModal.description || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Diagnosis
                    </label>
                    <textarea
                      name="diagnosis"
                      rows={3}
                      defaultValue={showEditModal.diagnosis || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Cause
                    </label>
                    <textarea
                      name="cause"
                      rows={2}
                      defaultValue={showEditModal.cause || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Symptoms
                    </label>
                    <textarea
                      name="symptoms"
                      rows={3}
                      defaultValue={showEditModal.symptoms || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Treatments
                    </label>
                    <textarea
                      name="treatments"
                      rows={3}
                      defaultValue={showEditModal.treatments || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Prevention
                    </label>
                    <textarea
                      name="prevention"
                      rows={3}
                      defaultValue={showEditModal.prevention || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Additional Notes
                    </label>
                    <textarea
                      name="notes"
                      rows={3}
                      defaultValue={showEditModal.notes || ''}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  {/* Affected Systems */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Affected Body Systems
                    </label>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {[
                        'Respiratory',
                        'Digestive',
                        'Nervous',
                        'Circulatory',
                        'Reproductive',
                        'Musculoskeletal',
                        'Integumentary',
                        'Urinary',
                        'Immune',
                        'Endocrine',
                      ].map((system) => (
                        <div key={system} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`edit-system-${system}`}
                            defaultChecked={showEditModal.affectedSystems?.includes(system) || false}
                            name={`affectedSystems-${system}`}
                            className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                          />
                          <label htmlFor={`edit-system-${system}`} className="ml-2 text-sm text-slate-700">
                            {system}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </BrandModalBody>
              <BrandModalFooter>
                <button
                  type="button"
                  onClick={() => setShowEditModal(null)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Disease
                </button>
              </BrandModalFooter>
            </form>
          </>
        ) : null}
      </BrandModal>
    </div>
  );
};

export default DiseasesList; 